import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import iso3166 from 'iso-3166-2';

export const DEFAULT_GEOFENCE = {
  countries: { mode: 'deny', codes: [] },
  states: { mode: 'deny', codes: [] },
};

// Built once from the ISO 3166 dataset already shipped with the app, so the
// picker is name-searchable while still storing canonical codes.
const COUNTRY_OPTIONS = Object.entries(iso3166.data)
  .map(([code, c]) => ({ code, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const STATE_OPTIONS = Object.entries(iso3166.data)
  .flatMap(([cc, c]) =>
    Object.entries(c.sub || {}).map(([code, s]) => ({ code, name: s.name, country: c.name, countryCode: cc }))
  )
  .sort((a, b) => a.country.localeCompare(b.country) || a.name.localeCompare(b.name));

const COUNTRY_BY_CODE = new Map(COUNTRY_OPTIONS.map((o) => [o.code, o]));
const STATE_BY_CODE = new Map(STATE_OPTIONS.map((o) => [o.code, o]));

// A country clears the country gate iff: allow-mode lists it (empty list = no
// constraint), or deny-mode doesn't. Geofence is AND, so offering states of a
// country that fails this gate would be pointless — they can never pass.
const countryClearsGate = (countryCode, mode, codes) =>
  mode === 'allow' ? (codes.length === 0 || codes.includes(countryCode)) : !codes.includes(countryCode);

// Cap rendered matches so the 5k-entry state list stays snappy.
const stateFilter = createFilterOptions({
  limit: 80,
  stringify: (o) => `${o.name} ${o.code} ${o.country}`,
});
const countryFilter = createFilterOptions({ stringify: (o) => `${o.name} ${o.code}` });

const toOptions = (codes, byCode) =>
  (codes || []).map((code) => byCode.get(code) || { code, name: code });

export default function GeofenceFields({ value, onChange, disabled = false }) {
  const { t } = useTranslation('shared');
  // Tolerate a partial/empty value (an existing link returns {} from the API),
  // so the levels and their `codes` arrays are always defined.
  const spec = {
    countries: { mode: 'deny', codes: [], ...(value?.countries || {}) },
    states: { mode: 'deny', codes: [], ...(value?.states || {}) },
  };

  const update = (level, patch) => onChange({ ...spec, [level]: { ...spec[level], ...patch } });

  // Changing the country gate can orphan state exceptions (e.g. switching from
  // allow-US to deny-US makes US-CA pointless), so drop states no longer permitted.
  const updateCountries = (patch) => {
    const countries = { ...spec.countries, ...patch };
    const codes = spec.states.codes.filter((sc) => countryClearsGate(sc.split('-')[0], countries.mode, countries.codes));
    onChange({ ...spec, countries, states: { ...spec.states, codes } });
  };

  const countryValue = useMemo(() => toOptions(spec.countries.codes, COUNTRY_BY_CODE), [spec.countries.codes]);
  const stateValue = useMemo(() => toOptions(spec.states.codes, STATE_BY_CODE), [spec.states.codes]);
  const stateOptions = useMemo(
    () => STATE_OPTIONS.filter((o) => countryClearsGate(o.countryCode, spec.countries.mode, spec.countries.codes)),
    [spec.countries.mode, spec.countries.codes],
  );

  const modeToggle = (level, title) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
      <RadioGroup row value={spec[level].mode}
        onChange={(e) => (level === 'countries' ? updateCountries({ mode: e.target.value }) : update(level, { mode: e.target.value }))}>
        <FormControlLabel value="allow" disabled={disabled} control={<Radio size="small" />}
          label={<Typography variant="body2">{t('geofence.allow')}</Typography>} />
        <FormControlLabel value="deny" disabled={disabled} control={<Radio size="small" />}
          label={<Typography variant="body2">{t('geofence.deny')}</Typography>} />
      </RadioGroup>
    </Box>
  );

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
        {t('geofence.helper')}
      </Typography>

      <Box sx={{ mt: 1.5 }}>
        {modeToggle('countries', t('geofence.countriesLabel'))}
        <Autocomplete
          multiple
          size="small"
          disabled={disabled}
          options={COUNTRY_OPTIONS}
          value={countryValue}
          filterOptions={countryFilter}
          isOptionEqualToValue={(o, v) => o.code === v.code}
          getOptionLabel={(o) => `${o.name} (${o.code})`}
          onChange={(_e, opts) => updateCountries({ codes: opts.map((o) => o.code) })}
          renderTags={(opts, getTagProps) =>
            opts.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.code} size="small" label={o.code} />)
          }
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder={t('geofence.searchPlaceholder')} />
          )}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        {modeToggle('states', t('geofence.statesLabel'))}
        <Autocomplete
          multiple
          size="small"
          disabled={disabled}
          options={stateOptions}
          value={stateValue}
          groupBy={(o) => o.country}
          filterOptions={stateFilter}
          isOptionEqualToValue={(o, v) => o.code === v.code}
          getOptionLabel={(o) => `${o.name} (${o.code})`}
          onChange={(_e, opts) => update('states', { codes: opts.map((o) => o.code) })}
          renderTags={(opts, getTagProps) =>
            opts.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.code} size="small" label={o.code} />)
          }
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder={t('geofence.searchPlaceholder')} />
          )}
        />
      </Box>
    </Box>
  );
}
